import { useMemo } from 'react';
import useAuth from './useAuth';
import { billingTypeOptions } from '../constants/asset';
import { getBillingTypesArray } from '../utils/billingHelpers';

export function getProjectStorageBillingTypeOptions(projectBillingTypes = null) {
  if (!projectBillingTypes) return null;

  return getBillingTypesArray(projectBillingTypes.storage)
    .filter(Boolean)
    .map(type => [type, type]);
}

export default function useFilteredBillingTypes(customOptions = null) {
  const { user } = useAuth();

  return useMemo(() => {
    const enabledBillingTypes = user?.company?.enabledBillingTypes || [];
    const optionsToFilter = customOptions || billingTypeOptions;

    // If no enabled billing types are set, show all options
    if (enabledBillingTypes.length === 0) {
      return optionsToFilter;
    }

    // Filter to only show enabled billing types
    return optionsToFilter.filter((option) => {
      const value = Array.isArray(option) ? option[0] : option.value;

      if (enabledBillingTypes.includes(value)) {
        return true;
      }

      // Special case: Backend "Time and Materials (T&M)" enables both frontend variants
      const isTimeAndMaterialsVariant = value === 'Time and Materials (Blended Rate)' || value === 'Time and Materials (Itemized Rates)';
      if (isTimeAndMaterialsVariant && enabledBillingTypes.includes('Time and Materials (T&M)')) {
        return true;
      }

      return false;
    });
  }, [user?.company?.enabledBillingTypes, customOptions]);
}

export function filterBillingTypesByCategory(billingTypes, category, enabledBillingTypes = []) {
  return billingTypes.filter(option => {
    const value = typeof option === 'object' && option.value ? option.value : option;

    // Category-specific exclusions
    if (category === 'storage' && value === 'Time and Materials (T&M)') {
      return false;
    }

    // Also exclude T&M variants from storage
    if (category === 'storage' && (value === 'Time and Materials (Blended Rate)' || value === 'Time and Materials (Itemized Rates)')) {
      return false;
    }

    if ((category === 'inbound' || category === 'outbound') && value === 'Storage Locations Occupied') {
      return false;
    }

    // Filter by company enabled billing types if provided
    if (enabledBillingTypes.length > 0) {
      if (enabledBillingTypes.includes(value)) {
        return true;
      }

      // Special case: Backend "Time and Materials (T&M)" enables both frontend variants
      const isTimeAndMaterialsVariant = value === 'Time and Materials (Blended Rate)' || value === 'Time and Materials (Itemized Rates)';
      if (isTimeAndMaterialsVariant && enabledBillingTypes.includes('Time and Materials (T&M)')) {
        return true;
      }

      return false;
    }

    return true;
  });
}
